"use client";

import { useLazyQuery } from "@apollo/client";
import { useOs } from "@mantine/hooks";
import { usePathname } from "next/navigation";
import { useEffect, useState } from "react";
import { useCareGachaAnnouncementDialog } from "@/components/model/careGacha/_function";
import { CareGachaAnnouncementDialog } from "@/components/model/careGacha/CareGachaAnnouncementDialog";
import { isEligibleForLineAppLinkPrompt } from "@/hooks/localStorage/useLineAppLinkPromptModal";
import {
  useAddressRegistrationModal,
  useGroupHomeNoteGuideModal,
  useLineAppLinkPromptModal,
  useNativeAppReleaseModal,
  usePenaltyRuleChangeModal,
  usePrivacyPolicyRevisionNoticeModal,
  useWorkIntentionSurveyModal,
} from "./_function";
import { notificationQuery } from "./_graphql";
import { AddressRegistrationModal } from "./AddressRegistrationModal";
import { AddToHomeScreen } from "./AddToHomeScreen";
import { FiveMinWorkReminder } from "./FiveMinWorkReminder";
import { GroupHomeNoteGuideModal } from "./GroupHomeNoteGuideModal";
import { LineAppLinkPromptModal } from "./LineAppLinkPromptModal";
import { NativeAppReleaseModal } from "./NativeAppReleaseModal";
import { PenaltyRuleChangeModal } from "./PenaltyRuleChangeModal";
import { PrivacyPolicyRevisionNoticeModal } from "./PrivacyPolicyRevisionNoticeModal";
import { WorkIntentionSurveyModal } from "./WorkIntentionSurveyModal";

export function WorkerNotification() {
  const [getNotifications, { data, loading, error }] =
    useLazyQuery(notificationQuery);
  const pathname = usePathname();
  const os = useOs();
  const isDesktop = os === "windows" || os === "macos" || os === "linux";

  const viewer = data?.viewer;

  const {
    shouldShowModal: shouldShowWorkIntentionSurvey,
    markDismissedForSession: markWorkIntentionSurveyDismissed,
    isLoaded: isLoadedWorkIntentionSurvey,
  } = useWorkIntentionSurveyModal(data, loading, error);

  const [workIntentionSurveyModalOpened, setWorkIntentionSurveyModalOpened] =
    useState(false);

  // 住所登録モーダルの状態管理
  const {
    shouldShowModal: shouldShowAddressModal,
    isLoaded: isLoadedAddressModal,
  } = useAddressRegistrationModal();

  const [addressModalOpened, setAddressModalOpened] = useState(false);

  // グループホームnoteガイドモーダルの状態管理
  const {
    shouldShowModal: shouldShowGroupHomeNoteGuideModal,
    isLoaded: isLoadedGroupHomeNoteGuideModal,
  } = useGroupHomeNoteGuideModal();

  const [groupHomeNoteGuideModalOpened, setGroupHomeNoteGuideModalOpened] =
    useState(false);

  // 遅刻・早退ペナルティルール変更モーダル（care_link_worker のみ。care_link_worker_mobile は対象外）
  const {
    shouldShowModal: shouldShowPenaltyRuleChangeModal,
    isLoaded: isLoadedPenaltyRuleChangeModal,
    setHideModal: setHidePenaltyRuleChangeModal,
  } = usePenaltyRuleChangeModal();

  const [penaltyRuleChangeModalOpened, setPenaltyRuleChangeModalOpened] =
    useState(false);

  // プライバシーポリシー改訂お知らせモーダル（care_link_worker のみ。care_link_worker_mobile は対象外）
  const {
    shouldShowModal: shouldShowPrivacyPolicyRevisionNoticeModal,
    isLoaded: isLoadedPrivacyPolicyRevisionNoticeModal,
    setHideModal: setHidePrivacyPolicyRevisionNoticeModal,
  } = usePrivacyPolicyRevisionNoticeModal();

  const [
    privacyPolicyRevisionNoticeModalOpened,
    setPrivacyPolicyRevisionNoticeModalOpened,
  ] = useState(false);

  // ネイティブアプリリリース告知モーダル（care_link_worker のみ。care_link_worker_mobile は対象外）
  const {
    shouldShowModal: shouldShowNativeAppReleaseModal,
    isLoaded: isLoadedNativeAppReleaseModal,
    setHideModal: setHideNativeAppReleaseModal,
  } = useNativeAppReleaseModal();

  const [nativeAppReleaseModalOpened, setNativeAppReleaseModalOpened] =
    useState(false);

  // LINE連携・アプリDL訴求ダイアログ（care_link_worker のみ。care_link_worker_mobile は対象外）
  const {
    shouldShowModal: shouldShowLineAppLinkPromptModal,
    isLoaded: isLoadedLineAppLinkPromptModal,
    setHideModal: setHideLineAppLinkPromptModal,
  } = useLineAppLinkPromptModal();

  const [lineAppLinkPromptModalOpened, setLineAppLinkPromptModalOpened] =
    useState(false);

  // ケアガチャ事前告知ダイアログ（care_link_worker のみ。care_link_worker_mobile は対象外）
  const {
    shouldShowDialog: shouldShowCareGachaDialog,
    markAsSeen: markCareGachaDialogSeen,
    isLoaded: isLoadedCareGachaDialog,
  } = useCareGachaAnnouncementDialog(viewer);

  const [careGachaDialogOpened, setCareGachaDialogOpened] = useState(false);

  // NativeAppReleaseModalとの二重表示を避けるための表示条件（D1）。
  // 新ダイアログ側のlocalStorage抑制状態には関係なく、条件を満たす間はNativeAppReleaseModalより優先する
  const isLineAppLinkPromptEligible = Boolean(
    !loading &&
      !error &&
      viewer &&
      isEligibleForLineAppLinkPrompt({
        hasLineAccount: viewer.hasLineAccount,
        hasDeviceToken: viewer.hasDeviceToken,
        hasProfile: viewer.hasProfile,
        hasAddress: viewer.hasAddress,
        isForcedWithdrawn: viewer.status === "forced_withdrawn",
      }),
  );

  const isWorkIntentionSurveyActive =
    isLoadedWorkIntentionSurvey &&
    !loading &&
    !error &&
    shouldShowWorkIntentionSurvey();

  const shouldSuppressOtherDialogs = workIntentionSurveyModalOpened;

  const addHomeScreenDisplayed = viewer ? viewer.addHomeScreenDisplayed : true;
  const hasAddress = viewer ? viewer.hasAddress : false;

  const isAddToHomeScreenShown = !isDesktop && !addHomeScreenDisplayed;

  // 住所登録モーダルは住所が未登録のときだけ描画される。ワーカー情報の取得前は
  // 住所なし扱いで opened が立つため、描画条件と組み合わせないと
  // 画面に出ていないモーダルを「開いている」と数えてしまう
  const isAddressModalShown = !hasAddress && addressModalOpened;

  // ケアガチャ事前告知ダイアログは他のダイアログと重ねて表示しない
  const isAnyOtherDialogOpened =
    shouldSuppressOtherDialogs ||
    penaltyRuleChangeModalOpened ||
    privacyPolicyRevisionNoticeModalOpened ||
    nativeAppReleaseModalOpened ||
    lineAppLinkPromptModalOpened ||
    isAddressModalShown ||
    groupHomeNoteGuideModalOpened ||
    isAddToHomeScreenShown;

  const isCareGachaDialogActive =
    isLoadedCareGachaDialog &&
    !loading &&
    !error &&
    shouldShowCareGachaDialog();

  useEffect(() => {
    let isSubscribed = true;
    const fetchNotifications = async () => {
      try {
        if (isSubscribed) {
          await getNotifications();
        }
      } catch (error) {
        console.error("Error fetching notifications:", error);
      }
    };
    fetchNotifications();
    return () => {
      isSubscribed = false;
    };
  }, [pathname, getNotifications]);

  // 勤務意向確認アンケート（最優先）
  useEffect(() => {
    if (isWorkIntentionSurveyActive) {
      setWorkIntentionSurveyModalOpened(true);
    }
  }, [isWorkIntentionSurveyActive]);

  // 住所登録モーダルの表示判定
  useEffect(() => {
    if (
      !shouldSuppressOtherDialogs &&
      isLoadedAddressModal &&
      shouldShowAddressModal(viewer?.hasAddress ?? false)
    ) {
      setAddressModalOpened(true);
    }
  }, [
    shouldSuppressOtherDialogs,
    isLoadedAddressModal,
    shouldShowAddressModal,
    viewer,
  ]);

  // グループホームnoteガイドモーダルの表示判定
  useEffect(() => {
    if (
      !shouldSuppressOtherDialogs &&
      isLoadedGroupHomeNoteGuideModal &&
      shouldShowGroupHomeNoteGuideModal()
    ) {
      setGroupHomeNoteGuideModalOpened(true);
    }
  }, [
    shouldSuppressOtherDialogs,
    isLoadedGroupHomeNoteGuideModal,
    shouldShowGroupHomeNoteGuideModal,
  ]);

  // 遅刻・早退ペナルティルール変更モーダルの表示判定
  useEffect(() => {
    if (
      !shouldSuppressOtherDialogs &&
      isLoadedPenaltyRuleChangeModal &&
      shouldShowPenaltyRuleChangeModal()
    ) {
      setPenaltyRuleChangeModalOpened(true);
    }
  }, [
    shouldSuppressOtherDialogs,
    isLoadedPenaltyRuleChangeModal,
    shouldShowPenaltyRuleChangeModal,
  ]);

  // プライバシーポリシー改訂お知らせモーダルの表示判定（ログイン済みワーカーのみ）
  useEffect(() => {
    if (
      !shouldSuppressOtherDialogs &&
      !loading &&
      !error &&
      viewer?.id &&
      isLoadedPrivacyPolicyRevisionNoticeModal &&
      shouldShowPrivacyPolicyRevisionNoticeModal()
    ) {
      setPrivacyPolicyRevisionNoticeModalOpened(true);
    }
  }, [
    shouldSuppressOtherDialogs,
    loading,
    error,
    viewer?.id,
    isLoadedPrivacyPolicyRevisionNoticeModal,
    shouldShowPrivacyPolicyRevisionNoticeModal,
  ]);

  // ネイティブアプリリリース告知モーダルの表示判定
  // LINE連携・アプリDL訴求ダイアログの表示条件（D1）を満たす間は二重表示を避けるため表示しない。
  // D1が確定する前（notificationQuery取得中）に開いてしまうと後から閉じる手段がないため、
  // 取得完了（!loading && !error）を待ってから判定する
  useEffect(() => {
    if (
      !shouldSuppressOtherDialogs &&
      !loading &&
      !error &&
      isLoadedNativeAppReleaseModal &&
      shouldShowNativeAppReleaseModal() &&
      !penaltyRuleChangeModalOpened &&
      !privacyPolicyRevisionNoticeModalOpened &&
      !isLineAppLinkPromptEligible
    ) {
      setNativeAppReleaseModalOpened(true);
    }
  }, [
    shouldSuppressOtherDialogs,
    loading,
    error,
    isLoadedNativeAppReleaseModal,
    shouldShowNativeAppReleaseModal,
    penaltyRuleChangeModalOpened,
    privacyPolicyRevisionNoticeModalOpened,
    isLineAppLinkPromptEligible,
  ]);

  // LINE連携・アプリDL訴求ダイアログの表示判定
  // LINE連携・アプリDL・プロフィール登録のいずれかのステータス取得に失敗した場合は表示しない
  useEffect(() => {
    if (
      !shouldSuppressOtherDialogs &&
      !loading &&
      !error &&
      isLoadedLineAppLinkPromptModal &&
      !penaltyRuleChangeModalOpened &&
      !privacyPolicyRevisionNoticeModalOpened &&
      shouldShowLineAppLinkPromptModal(isLineAppLinkPromptEligible)
    ) {
      setLineAppLinkPromptModalOpened(true);
    }
  }, [
    shouldSuppressOtherDialogs,
    loading,
    error,
    isLoadedLineAppLinkPromptModal,
    penaltyRuleChangeModalOpened,
    privacyPolicyRevisionNoticeModalOpened,
    isLineAppLinkPromptEligible,
    shouldShowLineAppLinkPromptModal,
  ]);

  // ケアガチャ事前告知ダイアログの表示判定
  // 他のダイアログと同じ評価タイミングで条件が揃うことがあり、同じコミット内では
  // 他ダイアログの opened がまだ false に見える。1 フレーム置いてから確定することで、
  // 同時に開こうとしている他ダイアログがある場合は再評価で見送る
  useEffect(() => {
    if (isAnyOtherDialogOpened) return;
    if (!isCareGachaDialogActive) return;

    const timer = window.setTimeout(() => {
      setCareGachaDialogOpened(true);
    }, 0);
    return () => window.clearTimeout(timer);
  }, [isAnyOtherDialogOpened, isCareGachaDialogActive]);

  const handlePenaltyRuleChangeConfirm = () => {
    setHidePenaltyRuleChangeModal(true);
    setPenaltyRuleChangeModalOpened(false);
  };

  const handlePrivacyPolicyRevisionNoticeConfirm = () => {
    setHidePrivacyPolicyRevisionNoticeModal(true);
    setPrivacyPolicyRevisionNoticeModalOpened(false);
  };

  const handleNativeAppReleaseConfirm = () => {
    setHideNativeAppReleaseModal(true);
    setNativeAppReleaseModalOpened(false);
  };

  const handleLineAppLinkPromptClose = () => {
    setHideLineAppLinkPromptModal(true);
    setLineAppLinkPromptModalOpened(false);
  };

  const handleCareGachaDialogClose = () => {
    markCareGachaDialogSeen();
    setCareGachaDialogOpened(false);
  };

  const handleWorkIntentionSurveyClose = () => {
    markWorkIntentionSurveyDismissed();
    setWorkIntentionSurveyModalOpened(false);
  };

  if (loading) {
    return (
      <>
        <WorkIntentionSurveyModal
          opened={workIntentionSurveyModalOpened}
          workerId={viewer?.id}
          onClose={handleWorkIntentionSurveyClose}
        />
        {!shouldSuppressOtherDialogs && (
          <>
            <PenaltyRuleChangeModal
              opened={penaltyRuleChangeModalOpened}
              onConfirm={handlePenaltyRuleChangeConfirm}
            />
            <PrivacyPolicyRevisionNoticeModal
              opened={privacyPolicyRevisionNoticeModalOpened}
              onConfirm={handlePrivacyPolicyRevisionNoticeConfirm}
            />
            <NativeAppReleaseModal
              opened={nativeAppReleaseModalOpened}
              onConfirm={handleNativeAppReleaseConfirm}
            />
            <LineAppLinkPromptModal
              opened={lineAppLinkPromptModalOpened}
              onClose={handleLineAppLinkPromptClose}
            />
            <CareGachaAnnouncementDialog
              opened={careGachaDialogOpened}
              onClose={handleCareGachaDialogClose}
            />
          </>
        )}
      </>
    );
  }

  return (
    <>
      <WorkIntentionSurveyModal
        opened={workIntentionSurveyModalOpened}
        workerId={viewer?.id}
        onClose={handleWorkIntentionSurveyClose}
      />
      {!shouldSuppressOtherDialogs && (
        <>
          <PenaltyRuleChangeModal
            opened={penaltyRuleChangeModalOpened}
            onConfirm={handlePenaltyRuleChangeConfirm}
          />
          <PrivacyPolicyRevisionNoticeModal
            opened={privacyPolicyRevisionNoticeModalOpened}
            onConfirm={handlePrivacyPolicyRevisionNoticeConfirm}
          />
          <NativeAppReleaseModal
            opened={nativeAppReleaseModalOpened}
            onConfirm={handleNativeAppReleaseConfirm}
          />
          <LineAppLinkPromptModal
            opened={lineAppLinkPromptModalOpened}
            onClose={handleLineAppLinkPromptClose}
          />
          <CareGachaAnnouncementDialog
            opened={careGachaDialogOpened}
            onClose={handleCareGachaDialogClose}
          />
          <FiveMinWorkReminder
            notificationId={viewer?.fiveMinWorkReminderDisplayedId}
          />
          {isAddToHomeScreenShown && <AddToHomeScreen />}
          {!hasAddress && (
            <AddressRegistrationModal
              opened={addressModalOpened}
              onClose={() => setAddressModalOpened(false)}
            />
          )}
          <GroupHomeNoteGuideModal
            opened={groupHomeNoteGuideModalOpened}
            onClose={() => setGroupHomeNoteGuideModalOpened(false)}
          />
        </>
      )}
    </>
  );
}
